import React from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { compactActionSx, money } from "./ownerCenterUi";

function dateTimeInputNow() {
  const date = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function OwnerRecurringRecordDialog({ open, onClose, plan, accounts = [], onSaved }) {
  const [amount, setAmount] = React.useState("");
  const [accountId, setAccountId] = React.useState("");
  const [occurredAt, setOccurredAt] = React.useState(dateTimeInputNow());
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const eligibleAccounts = accounts.filter((account) => !account.archived && account.currency === plan?.currency);
  React.useEffect(() => {
    if (!open || !plan) return;
    const suggested = plan.amountMode === "fixed" ? plan.fixedAmount : plan.projectedAmount;
    setAmount(suggested ? String(Number(suggested)) : "");
    setAccountId(plan.defaultAccountId || eligibleAccounts[0]?.id || eligibleAccounts[0]?._id || "");
    setOccurredAt(dateTimeInputNow());
    setError("");
  }, [open, plan]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSaving(true); setError("");
    try {
      await ownerApi.recordRecurringFinance(plan.id || plan._id, {
        periodKey: plan.nextPeriodKey,
        amount: Number(amount),
        accountId,
        occurredAt,
      });
      onSaved?.(); onClose?.();
    } catch (err) {
      setError(formatApiError(err, "No pude registrar este mensual."));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography component="div" sx={{ fontWeight: 1000, fontSize: "1.1rem" }}>{plan?.direction === "income" ? "Registrar cobro mensual" : "Registrar pago mensual"}</Typography>
        <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".82rem", mt: .3 }}>{plan?.name} · período {plan?.nextPeriodKey || "actual"}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.35}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {plan?.amountMode === "variable" ? <Alert severity="info">Monto variable: cargá lo que ocurrió realmente. El promedio se recalcula solo después de guardar.</Alert> : null}
          <TextField autoFocus fullWidth type="number" label={`Monto real · ${plan?.currency || ""}`} value={amount} onChange={(event) => setAmount(event.target.value)} helperText={plan?.history?.sampleCount ? `Promedio actual: ${money(plan.history.average, plan.currency)} · ${plan.history.sampleCount} registros` : "Todavía no hay historial real para este mensual."} />
          <FormControl fullWidth><InputLabel>Cuenta</InputLabel><Select label="Cuenta" value={accountId} onChange={(event) => setAccountId(event.target.value)}>{eligibleAccounts.map((account) => <MenuItem key={account.id || account._id} value={account.id || account._id}>{account.name} · {account.currency}</MenuItem>)}</Select></FormControl>
          <TextField fullWidth type="datetime-local" label="Cuándo ocurrió" InputLabelProps={{ shrink: true }} value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.6 }}>
        <Button onClick={onClose} disabled={saving} sx={compactActionSx}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !(Number(amount) > 0) || !accountId || !plan?.nextPeriodKey} sx={{ ...compactActionSx, px: 2.2 }}>{saving ? "Registrando…" : "Registrar y actualizar promedio"}</Button>
      </DialogActions>
    </Dialog>
  );
}
